/**
 * Centralized constants for the CanIDeal marketing site.
 * SINGLE SOURCE OF TRUTH — import from here, never hardcode.
 */

import type {
  FooterSection,
  HubSpotFormConfig,
  HubSpotFormKey,
  NavItem,
  NavRouteLink,
  NavSignupLink,
} from '@/lib/types';
// Plain JSON so next.config.mjs and next-sitemap.config.js read the same values.
import siteUrls from '@/lib/siteUrls.json';

// ─── URLs ────────────────────────────────────────────────────────────────────

/** Base URL for the CanIDeal application (backend + Vue app) */
export const APP_BASE = process.env.NEXT_PUBLIC_API_URL || siteUrls.appBase;

/** Base URL for the marketing site (this site): canonicals, sitemap, structured data. */
export const SITE_URL = siteUrls.siteUrl;

// ─── Routes ──────────────────────────────────────────────────────────────────
// Every internal page the shared header/footer can reach. Pages that exist on
// canideal.org but not yet on canideal.com map to the closest existing route.

export const ROUTES = {
  home: '/',
  terminal: '/terminal',
  marketsPricing: '/markets-pricing',
  cropRoi: '/crop',
  iq: '/canideal-iq',
  marketBriefs: '/market-briefs',
  sellProducts: '/sell-products',
  sourceProducts: '/source-products',
  howItWorks: '/how-it-works',
  support: '/support',
  compliance: '/compliance',
  safety: '/how-we-keep-you-safe',
  terms: '/terms',
  privacy: '/privacy',
} as const;

// ─── Navigation (approved canideal.org nav) ─────────────────────────────────

export const INTELLIGENCE_LINKS: NavRouteLink[] = [
  { label: 'Markets & Pricing', href: ROUTES.marketsPricing },
  { label: 'Crop ROI Planner', href: ROUTES.cropRoi },
  { label: 'CanIDeal IQ', href: ROUTES.iq },
  { label: 'Market Briefs & Podcast', href: ROUTES.marketBriefs },
];

/**
 * Sales before buying. As on canideal.org: the homepage opens the registration
 * dialog (the page route is the no-JS / new-tab fallback); inner pages link to
 * the Sell / Source Products pages.
 */
export const TRADE_LINKS: NavSignupLink[] = [
  { label: 'Sell Products', signup: 'vendor', href: ROUTES.sellProducts },
  { label: 'Source Products', signup: 'retailer', href: ROUTES.sourceProducts },
];

/** Also rendered as the in-page navigation on every Tools & Trust page. */
export const TOOLS_TRUST_LINKS: NavRouteLink[] = [
  { label: 'How CanIDeal Works', href: ROUTES.howItWorks },
  { label: 'Customer Support', href: ROUTES.support },
  { label: 'Compliance', href: ROUTES.compliance },
  { label: 'How We Keep You Safe', href: ROUTES.safety },
];

export const NAV_LINKS: NavItem[] = [
  { label: 'Home', href: ROUTES.home },
  { label: 'Terminal', href: ROUTES.terminal },
  { label: 'Intelligence', links: INTELLIGENCE_LINKS },
  { label: 'Trade', links: TRADE_LINKS },
  { label: 'Tools & Trust', links: TOOLS_TRUST_LINKS },
];

/**
 * Homepage masthead (approved homepage.html): Terminal is a one-item menu
 * ("Open Terminal →") instead of a plain link; every other item is NAV_LINKS.
 */
export const HOME_NAV_LINKS: NavItem[] = NAV_LINKS.map((item) =>
  item.label === 'Terminal' && 'href' in item
    ? { label: item.label, links: [{ label: 'Open Terminal →', href: item.href }] }
    : item
);

export const LOGIN_LINK: NavRouteLink = { label: 'Log in', href: `${APP_BASE}/login` };
export const REGISTER_LINK: NavSignupLink = { label: 'Register', signup: 'choose' };

/** Full footer (homepage). Terms & Privacy use the canideal.com legal pages. */
export const FOOTER_SECTIONS: FooterSection[] = [
  { title: 'Intelligence', links: INTELLIGENCE_LINKS },
  { title: 'Trade', links: TRADE_LINKS },
  { title: 'Tools & Trust', links: TOOLS_TRUST_LINKS },
  {
    title: 'CanIDeal',
    links: [
      { label: 'Terms', href: ROUTES.terms },
      { label: 'Privacy', href: ROUTES.privacy },
      { label: 'Data Methodology', href: `${ROUTES.howItWorks}#methodology` },
      { label: 'Commercial Disclosures', href: `${ROUTES.terminal}#commercial-disclosures` },
    ],
  },
];

/** Compact footer (inner pages). */
export const FOOTER_COMPACT_LINKS: NavRouteLink[] = [
  { label: 'Methodology', href: `${ROUTES.howItWorks}#methodology` },
  { label: 'Compliance', href: ROUTES.compliance },
  { label: 'How We Keep You Safe', href: ROUTES.safety },
  { label: 'Support', href: ROUTES.support },
];

export const BRAND_TAGLINE = 'Global intelligence. Commercial action.';

/** Approved homepage title — also the default social-card headline. */
export const SITE_TITLE = 'CanIDeal — Global Cannabis Commodities Terminal and Exchange';

/**
 * Dark-theme brand colours for renderers that cannot read CSS variables (the
 * generated social card, the browser `theme-color`). Mirror `--cid-bg`,
 * `--cid-cyan`, `--cid-text`, `--cid-muted` in src/styles/tailwind.css.
 */
export const BRAND_COLORS = {
  bg: '#001e2d',
  accent: '#00e9df',
  text: '#f4f8fc',
  muted: '#adc1cf',
} as const;

/** Generated social card (src/app/opengraph-image.tsx), 1200×630. */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/opengraph-image`;

/** Approved wordmark + flower sprite (canideal.org assets/homepage/brand-2.webp). */
export const BRAND_SPRITE = '/assets/brand/brand-2.webp';

// ─── Theme ───────────────────────────────────────────────────────────────────

/** localStorage key for the visitor's light/dark choice (next-themes). */
export const THEME_STORAGE_KEY = 'theme';

// ─── HubSpot ─────────────────────────────────────────────────────────────────

export const HUBSPOT_PORTAL_ID = '243062194';
export const HUBSPOT_REGION = 'na2';
/** Regional tracking-code loader (cookie banner + visitor tracking), UI-verified on .org. */
export const HUBSPOT_TRACKING_SRC = `https://js-${HUBSPOT_REGION}.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;

export const HUBSPOT_FORMS: Record<HubSpotFormKey, HubSpotFormConfig> = {
  vendor: { id: '3a702ab2-e6df-4818-912d-e68da3d4b502', embed: 'v2' },
  retailer: { id: 'be4dc84a-a235-4dda-9c68-c29b219f6a3a', embed: 'v2' },
  /** Market Briefs email subscription (separate from marketplace registration). */
  newsletter: { id: '7afaeb3c-7e33-4837-990c-5dc74eb8e302', embed: 'v2' },
  support: {
    id: '4d564ef5-35bc-48ee-9290-93d4c06f22d7',
    embed: 'v4',
    hostedUrl: 'https://share-na2.hsforms.com/2TVZO9TW8SO6SkJPUwG8i1w40pnzm',
  },
};

/** HubSpot Help Desk inbox (used as the support fallback). */
export const SUPPORT_EMAIL = `support-1@${HUBSPOT_PORTAL_ID}.${HUBSPOT_REGION}.r.hubspot-inbox.com`;

// ─── Market Briefs ───────────────────────────────────────────────────────────

/** Byline on every published brief (visible + Article structured data). */
export const BRIEFS_BYLINE = 'CanIDeal Market Intelligence';
/** Published-only RSS feed (rewritten to the backend in next.config.mjs). */
export const BRIEFS_FEED_PATH = '/market-briefs/feed.xml';
/** Same-origin edition JSON the briefs page re-reads on Refresh (rewritten to APP_BASE). */
export const BRIEFS_EDITION_PATH = '/api/market/public/briefs';

// ─── Signal Colors ───────────────────────────────────────────────────────────
// Used for market signal badges/text throughout the site.

export type SignalType = 'STRONG BUY' | 'BUY' | 'WAIT' | 'SELL' | 'STRONG SELL' | 'WATCH';

/** Text-only signal colors — theme-aware (uses semantic tokens in dark mode) */
export const SIGNAL_TEXT_COLORS: Record<string, string> = {
  'STRONG BUY': 'text-green-700 dark:text-accent',
  BUY: 'text-green-600 dark:text-accent',
  WAIT: 'text-muted-foreground',
  SELL: 'text-red-600 dark:text-error',
  'STRONG SELL': 'text-red-700 dark:text-error',
  WATCH: 'text-blue-500 dark:text-blue-400',
};

/** Background + text signal colors (for badges, pills) — theme-aware */
export const SIGNAL_BG_COLORS: Record<string, string> = {
  'STRONG BUY': 'bg-green-600 dark:bg-accent text-white',
  BUY: 'bg-green-500 dark:bg-accent text-white',
  WAIT: 'bg-yellow-500 dark:bg-warning text-gray-900 dark:text-warning-foreground',
  SELL: 'bg-red-500 dark:bg-error text-white',
  'STRONG SELL': 'bg-red-600 dark:bg-error text-white',
  WATCH: 'bg-blue-500 dark:bg-blue-600 text-white',
};

/** Bias text colors — theme-aware */
export const BIAS_COLORS: Record<string, string> = {
  BUY: 'text-green-600 dark:text-accent',
  'STRONG BUY': 'text-green-700 dark:text-accent',
  SELL: 'text-red-600 dark:text-error',
  'STRONG SELL': 'text-red-700 dark:text-error',
  WAIT: 'text-muted-foreground',
  WATCH: 'text-blue-500 dark:text-blue-400',
};
